import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner } from "react-bootstrap";
import ActionComp from "../layouts/ActionComp";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import GridMaster from "./GridMaster";
import StatusRenderer from "./StatusRenderer.jsx";
import ActionRenderer from "./ActionRenderer";
import { exportToExcel } from "../utils/ExcelCreation";
import { formatDateInDDMMYYYY } from "../../functions/common-functions";
import AddEditLocationMaster from "./AddEditLocationMaster";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";
import { AuthContext } from "../../contexts/AuthContext";
import { menuActions } from "../../store/menu-slice";
import { useDispatch } from "react-redux";
import { sortByPropertyInAscending } from "../../utils/Utility";
import { searchActions } from "../../store/grid-slice";
import { ALERT_PARAMS } from "../../utils/constant.js";
import { deleteLocation, getAllLocations, checkLocationUsage } from "../../services/location-master-service.js";
import { addLogDetails } from "../../services/logger-entry-service.js";

const LocationMaster = () => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const userDetails = { userName: context.userName, role: context.role, userId: context.userId };

  const dispatch = useDispatch();

  const [rowData, setRowData] = useState({});
  const [filteredResult, setFilteredResult] = useState([]);
  const [filteredResulttemp, setFilteredResulttemp] = useState([]);
  const [filterColLength, setFilterColLength] = useState(0);
  const [action, setAction] = useState("");
  const [reload, setReload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [resetFilter, setResetFilter] = useState(true);
  const [loading, setLoading] = useState(true);

  const columnDefs = [
    {
      headerName: "Status",
      field: "DeleteIndication",
      flex: false,
      width: 150,
      resizable: true,
      tooltipField: "DeleteIndication",
      cellRenderer: StatusRenderer,
      sort: 'asc'
    },
    {
      headerName: "Division",
      field: "Division",
      width: 200,
      tooltipField: "Division",
    },
    {
      headerName: "Location",
      field: "Location",
      tooltipField: "Location",
      width: 200,
      sort: 'asc'
    },
    {
      headerName: "Inserted By",
      field: "CreatedByName",
      width: 200,
      tooltipField: "CreatedByName",
    },
    {
      headerName: "Inserted On",
      field: "CreatedOn",
      width: 200,
      tooltipField: "CreatedOn",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      }
    },
    {
      headerName: "Modified By",
      field: "ModifiedByName",
      width: 200,
      tooltipField: "ModifiedByName",
    },
    {
      headerName: "Modified On",
      field: "ModifiedOn",
      width: 200,
      tooltipField: "ModifiedOn",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      }
    },
    {
      field: "",
      width: 120,
      cellRenderer: function (params) {
        return <ActionRenderer
          selectedData={params.data}
          handleViewEvent={handleView}
          handleEditEvent={handleEdit}
          handleDeleteEvent={handleDelete} />
      },
      sortable: false,
      filter: false,
      pinned: 'right',
      resizable: false
    },
  ];

  const headerName = (field) => {
    let name;
    switch (field) {
      case "DeleteIndication":
        name = "Status";
        break;
      case "Division":
        name = "Division";
        break;
      case "Location":
        name = "Location";
        break;
      case "CreatedByName":
        name = "Inserted By";
        break;
      case "CreatedOn":
        name = "Inserted On";
        break;
      case "ModifiedByName":
        name = "Modified By";
        break;
      case "ModifiedOn":
        name = "Modified On";
        break;
      default:
        name = "No match found";
        break;
    }
    return name;
  };

  const fieldName = (header) => {
    let name;
    switch (header) {
      case "Status":
        name = "DeleteIndication";
        break;
      case "Division":
        name = "Division";
        break;
      case "Location":
        name = "Location";
        break;
      case "Inserted By":
        name = "CreatedByName";
        break;
      case "Inserted On":
        name = "CreatedOn";
        break;
      case "Modified By":
        name = "ModifiedByName";
        break;
      case "Modified On":
        name = "ModifiedOn";
        break;
      default:
        name = "No match found";
        break;
    }
    return name;
  };

  const fetchLocationMaster = async () => {
    setLoading(true);
    const result = await getAllLocations();
    const data = Array.isArray(result) ? result : [];
    data.sort(sortByPropertyInAscending("DeleteIndication"));
    setFilteredResult(data);
    setExcelData(data);
    setResetFilter(!resetFilter);
    setLoading(false);
  };

  useEffect(() => {
    dispatch(menuActions.setMenu("Location Master"));
    dispatch(searchActions.setSearch(""));
    fetchLocationMaster();
  }, [reload]);

  const handleView = (row) => {
    setAction("View");
    setRowData(row);
    setShowForm(true);
  }

  const handleEdit = (row) => {
    setAction("Edit");
    setRowData(row);
    setShowForm(true);
  }

  const handleDelete = async (row) => {
    setLoading(true);
    
    try {
      const usageData = await checkLocationUsage(row.Location);
      setLoading(false);
      
      let confirmMessage = "";
      let deleteType = "physical";
      
      if (usageData.usedInDisposal || usageData.usedInEnquiry) {
        if (usageData.usedInUserMaster) {
          confirmMessage = "Location is used in Disposal/Enquiry and linked to users. It will be marked as Inactive and delinked from users.";
        } else {
          confirmMessage = "Location is used in Disposal Intimation or Enquiry and cannot be deleted.";
        }
        deleteType = "logical";
      } else if (usageData.usedInUserMaster) {
        confirmMessage = "This location is linked to multiple users. Deleting will delink the location to the users.";
        deleteType = "logical";
      } else {
        confirmMessage = "Are you sure, do you want to permanently delete Location " + row.Location + "?";
        deleteType = "physical";
      }
      
      MySwal.fire({
        text: confirmMessage,
        confirmButtonText: "Delete",
        title: "Delete Location",
        ...ALERT_PARAMS
      }).then(async (result) => {
        if (result.isConfirmed) {
          setLoading(true);
          
          deleteLocation(row.SK, deleteType)
            .then(async () => {
              await addLogDetails(row, "Delete", row.SK, "LocationMaster", userDetails, "Master");
              setLoading(false);
              const successMessage = deleteType === "physical"
                ? "Location permanently deleted."
                : (usageData.usedInDisposal || usageData.usedInEnquiry) && usageData.usedInUserMaster
                  ? "Location status updated to Inactive and delinked from users."
                  : usageData.usedInUserMaster
                    ? "Location status updated to Inactive and delinked from users."
                    : "Location status updated to Inactive.";
              MySwal.fire({
                text: successMessage,
                imageUrl: successImage,
                customClass: "swal-success",
                didClose: () => {
                  setReload(!reload);
                },
              });
            })
            .catch((error) => {
              setLoading(false);
              MySwal.fire({
                text: "Unable to process request.",
                imageUrl: errorImage,
                customClass: "swal-success",
              });
            });
        }
      });
    } catch (error) {
      setLoading(false);
      MySwal.fire({
        text: "Unable to check location usage.",
        imageUrl: errorImage,
        customClass: "swal-success",
      });
    }
  }

  const handleGlobalExport = async () => {
    let filename = "LocationMaster";
    let excelDownoadData = [];

    excelData.forEach(element => {
      let dataEntry = {
        DeleteIndication: element.DeleteIndication,
        Division: element.Division,
        Location: element.Location,
        CreatedByName: element.CreatedByName,
        CreatedOn: element.CreatedOn ? formatDateInDDMMYYYY(element.CreatedOn.toString()) : "",
        ModifiedByName: element.ModifiedByName,
        ModifiedOn: element.ModifiedOn ? formatDateInDDMMYYYY(element.ModifiedOn.toString()) : ""
      };
      excelDownoadData.push(dataEntry);
    });

    let colName = ["Status", "Division", "Location", "Inserted By", "Inserted On", "Modified By", "Modified On"];
    let Fieldname = ["DeleteIndication", "Division", "Location", "CreatedByName", "CreatedOn", "ModifiedByName", "ModifiedOn"];
    exportToExcel(filename, "Data", excelDownoadData, "A1:G1", colName, Fieldname);
  };

  const handleAdd = () => {
    setAction("Add");
    setShowForm(true);
  };

  return (
    <>
      {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <ActionComp
          label={"Add New Location"}
          exportEvent={() => handleGlobalExport()}
          handleAdd={handleAdd} />

        <GridMaster
          rowDetails={filteredResult}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          getDataEvent={(getFilteredData) => setFilteredResulttemp(getFilteredData)}
          getFilterColLength={(getLength) => setFilterColLength(getLength)}
          setExcelData={setExcelData}
          handleView={handleView}
          handleEdit={handleEdit}
          openConfirmBox={handleDelete}
          resetind={resetFilter}
        />
        {showForm && (
          <AddEditLocationMaster
            show={showForm}
            action={action}
            rowdata={rowData}
            onHide={() => setShowForm(false)}
            setreload={setReload}
            reload={reload}
          />
        )}
      </Container>
    </>
  );
};

export default LocationMaster;